/**
 * useYearEndGallery.js
 *
 * Custom hook for fetching and managing Year-End Gallery data
 * Supports infinite scroll
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchYearEnds } from '../utils/yearEndGalleryUtils';

/**
 * Hook for Year-End Gallery data management
 *
 * @param {Object} options
 * @param {number} options.year - Year filter (default: 2025)
 * @returns {Object} Gallery state and methods
 */
export function useYearEndGallery({ year = 2025 } = {}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  // Use ref for lastDoc to avoid stale closure issues
  const lastDocRef = useRef(null);

  /**
   * Load initial data
   */
  const loadInitial = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    lastDocRef.current = null;

    try {
      const result = await fetchYearEnds({
        lastDoc: null,
        year
      });

      setItems(result.items);
      lastDocRef.current = result.lastDoc;
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('[useYearEndGallery] loadInitial error:', err);
      setError(err);
      setItems([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [year]);

  /**
   * Load more items (pagination)
   */
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || isLoading) return;

    setIsLoadingMore(true);

    try {
      const result = await fetchYearEnds({
        lastDoc: lastDocRef.current,
        year
      });

      setItems(prev => [...prev, ...result.items]);
      lastDocRef.current = result.lastDoc;
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('[useYearEndGallery] loadMore error:', err);
      setError(err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, isLoading, year]);

  /**
   * Refresh data
   */
  const refresh = useCallback(() => {
    loadInitial();
  }, [loadInitial]);

  return {
    // State
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,

    // Actions
    loadInitial,
    loadMore,
    refresh
  };
}

/**
 * Hook for infinite scroll detection
 * Returns a callback ref to attach to the sentinel element
 *
 * @param {Object} options
 * @param {Function} options.onIntersect - Callback when element is visible
 * @param {boolean} options.enabled - Whether to observe (default: true)
 * @param {number} options.rootMargin - Margin around root (default: '100px')
 * @returns {Function} Callback ref
 */
export function useInfiniteScrollObserver({
  onIntersect,
  enabled = true,
  rootMargin = '100px'
} = {}) {
  const observerRef = useRef(null);

  const setRef = useCallback((node) => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!node || !enabled) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && onIntersect) {
          onIntersect();
        }
      },
      {
        rootMargin,
        threshold: 0
      }
    );

    observerRef.current.observe(node);
  }, [enabled, onIntersect, rootMargin]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return setRef;
}

export default useYearEndGallery;
